import { getCurrencyExponent } from '@/lib/moneyUtils';

import type { TaskMoney, TaskMoneyInput, TaskUtils, TaskWorkspaceFields } from './types';
import { caughtErrorDetail, warnSdkUtilFallback } from './warn';

const DEFAULT_CURRENCY = 'usd';

const moneyFormatters = new Map<string, Intl.NumberFormat>();

function moneyFormatter(currency: string): Intl.NumberFormat {
  let formatter = moneyFormatters.get(currency);

  if (!formatter) {
    formatter = new Intl.NumberFormat('en-US', { style: 'currency', currency });
    moneyFormatters.set(currency, formatter);
  }

  return formatter;
}

function isMoney(value: unknown): value is TaskMoney {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as TaskMoney).amount === 'number' &&
    Number.isFinite((value as TaskMoney).amount) &&
    typeof (value as TaskMoney).currency === 'string' &&
    (value as TaskMoney).currency !== ''
  );
}

function requireMoney(value: TaskMoneyInput, operation: string): TaskMoney {
  if (!isMoney(value)) {
    throw new Error(`Sdk.utils.money.${operation} requires a valid money value.`);
  }

  return value;
}

export function addMoney(a: TaskMoneyInput, b: TaskMoneyInput): TaskMoney | null {
  if (a === null || a === undefined || b === null || b === undefined) {
    return null;
  }

  const augend = requireMoney(a, 'add');
  const addend = requireMoney(b, 'add');

  if (augend.currency !== addend.currency) {
    throw new Error(
      `Cannot operate on different currencies: ${augend.currency} and ${addend.currency}.`
    );
  }

  return { amount: augend.amount + addend.amount, currency: augend.currency };
}

export function subtractMoney(a: TaskMoneyInput, b: TaskMoneyInput): TaskMoney | null {
  if (a === null || a === undefined || b === null || b === undefined) {
    return null;
  }

  const minuend = requireMoney(a, 'subtract');
  const subtrahend = requireMoney(b, 'subtract');

  if (minuend.currency !== subtrahend.currency) {
    throw new Error(
      `Cannot operate on different currencies: ${minuend.currency} and ${subtrahend.currency}.`
    );
  }

  return { amount: minuend.amount - subtrahend.amount, currency: minuend.currency };
}

export function multiplyMoney(value: TaskMoneyInput, factor: number): TaskMoney | null {
  if (value === null || value === undefined) {
    return null;
  }

  const money = requireMoney(value, 'multiply');

  return {
    amount: Math.round(money.amount * (factor ?? 0)),
    currency: money.currency
  };
}

export function divideMoney(value: TaskMoneyInput, divisor: number): TaskMoney | null {
  if (value === null || value === undefined) {
    return null;
  }

  const money = requireMoney(value, 'divide');

  if (!divisor) {
    warnSdkUtilFallback(
      'money.divide',
      `The divisor is ${String(divisor)}; returning a zero amount in ${money.currency}.`
    );
    return { amount: 0, currency: money.currency };
  }

  return {
    amount: Math.round(money.amount / divisor),
    currency: money.currency
  };
}

export function percentageMoney(value: TaskMoneyInput, percent: number): TaskMoney | null {
  if (value === null || value === undefined) {
    return null;
  }

  const money = requireMoney(value, 'percentage');

  return {
    amount: Math.round((money.amount * (percent ?? 0)) / 100),
    currency: money.currency
  };
}

export function sumMoney(values: unknown, fallbackCurrency: string = DEFAULT_CURRENCY): TaskMoney {
  if (!Array.isArray(values)) {
    warnSdkUtilFallback(
      'money.sum',
      `Expected an array of money values; returning a zero amount in ${fallbackCurrency}.`
    );
    return { amount: 0, currency: fallbackCurrency };
  }

  const source = values;
  const list = source.filter(isMoney);

  if (list.length !== source.length) {
    warnSdkUtilFallback(
      'money.sum',
      `${source.length - list.length} nullish or malformed money value(s) were skipped.`
    );
  }

  if (list.length === 0) {
    return { amount: 0, currency: fallbackCurrency };
  }

  const currency = list[0].currency;

  return list.reduce<TaskMoney>(
    (acc, item) => {
      if (item.currency !== acc.currency) {
        throw new Error(
          `Cannot operate on different currencies: ${acc.currency} and ${item.currency}.`
        );
      }
      return { amount: acc.amount + item.amount, currency: acc.currency };
    },
    { amount: 0, currency }
  );
}

export function formatMoney(value: TaskMoneyInput): string | null {
  if (!isMoney(value)) {
    warnSdkUtilFallback('money.format', 'The value is not valid money; returning null.');
    return null;
  }

  try {
    const currency = value.currency.toUpperCase();
    const exponent = getCurrencyExponent(currency);
    const major = exponent === 0 ? value.amount : value.amount / 10 ** exponent;

    return moneyFormatter(currency).format(major);
  } catch (error) {
    warnSdkUtilFallback(
      'money.format',
      `The value could not be formatted: ${caughtErrorDetail(error)}. Returning null.`
    );
    return null;
  }
}

export function createMoneyUtils(workspaceFields: TaskWorkspaceFields): TaskUtils['money'] {
  function defaultCurrency(): string {
    const value = workspaceFields['default_currency'];
    return typeof value === 'string' && value.length > 0 ? value : DEFAULT_CURRENCY;
  }

  return {
    add: addMoney,
    subtract: subtractMoney,
    multiply: multiplyMoney,
    divide: divideMoney,
    percentage: percentageMoney,
    sum: (values) => sumMoney(values, defaultCurrency()),
    format: formatMoney
  };
}
